import React, { useCallback, useState } from 'react';
import PageHero from '../components/PageHero';
import TurnstileWidget from '../components/TurnstileWidget';
import { useContent } from '../content/ContentContext';
import { useUi } from '../i18n/ui';
import { renderMultiline } from '../utils/text';
import { publicApi } from '../api/insolita';

// `website` è il campo honeypot: nascosto via CSS, i bot lo compilano.
const emptyForm = { firstName: '', lastName: '', phone: '', email: '', message: '', consent: false, website: '' };

const Iscrizioni: React.FC = () => {
  const { t, img } = useContent();
  const { tr } = useUi();
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const handleCaptcha = useCallback((token: string | null) => setCaptchaToken(token), []);

  const set = (k: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: k === 'consent' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!captchaToken) {
      setError(tr('if.captchaMissing') || 'Completa la verifica anti-bot prima di inviare.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await publicApi.createMembership({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone || undefined,
        message: form.message || undefined,
        consent: form.consent,
        captchaToken,
        website: form.website,
      });
      setSent(true);
      setForm(emptyForm);
      setCaptchaToken(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || tr('if.err'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero image={img('iscrizioni.hero.image', '/img/insolita-sardegna-storia.jpg')} crumb={tr('page.iscrizioni.title')} title={tr('page.iscrizioni.title')} minHeight="52svh" />

      <section className="sec" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <div className="split">
            <div className="reveal">
              <span className="eyebrow">{t('iscrizioni.eyebrow', 'Tesseramento FMI 2026')}</span>
              <h2 className="h-sec" style={{ margin: '.35em 0 .6em' }}>{renderMultiline(t('iscrizioni.title', 'Unisciti\na noi'))}</h2>
              <p className="lead" style={{ marginBottom: '1.1em' }}>{t('iscrizioni.lead1', "Siamo entusiasti di aprire il tesseramento per la Federazione Motociclistica Italiana (FMI). Un'opportunità imperdibile per tutti gli amanti delle due ruote che desiderano vivere una stagione indimenticabile e partecipare alle gare in programma.")}</p>
              <p className="lead">{t('iscrizioni.lead2', 'Entra a far parte di una comunità appassionata, accedi a tutte le gare ufficiali FMI e vivi la passione per le due ruote in un ambiente di supporto e amicizia.')}</p>
            </div>
            <div className="split__media reveal" data-d="1">
              <img src={img('iscrizioni.split.image', '/img/insolita-sardegna-experience.jpg')} alt="Community Insolita Sardegna" />
              <span className="frame" />
              <span className="tag">Membership 2026</span>
            </div>
          </div>
        </div>
      </section>

      <section className="sec">
        <div className="wrap">
          <div className="split" style={{ alignItems: 'start' }}>
            <div className="reveal">
              <span className="eyebrow">{t('iscrizioni.costs.eyebrow', 'Costi tesseramento')}</span>
              <div className="price-rows" style={{ marginTop: 28 }}>
                <div className="row"><span><b>{t('iscrizioni.cost1.name', 'Tesseramento FMI')}</b><small>{t('iscrizioni.cost1.desc', 'Accesso alla community e alle convenzioni')}</small></span><span className="p">{t('iscrizioni.cost1.price', '€45')}</span></div>
                <div className="row"><span><b>{t('iscrizioni.cost2.name', 'Licenza Gare 2026')}</b><small>{t('iscrizioni.cost2.desc', 'Per partecipare alle competizioni ufficiali')}</small></span><span className="p">{t('iscrizioni.cost2.price', '€200')}</span></div>
              </div>
              <p style={{ marginTop: 22, color: 'var(--gray-d)', fontSize: '.9rem' }}>{t('iscrizioni.costs.note', 'Scadenza tesseramento: 15 Febbraio · Pagamento via bonifico bancario.')}</p>
            </div>
            <div className="reveal" data-d="1">
              <span className="eyebrow">{t('iscrizioni.steps.eyebrow', 'Come tesserarsi')}</span>
              <div className="steps">
                <div className="step"><h4>{t('iscrizioni.step1.title', 'Compila il form')}</h4><p>{t('iscrizioni.step1.text', 'Inserisci i tuoi dati nel modulo qui sotto.')}</p></div>
                <div className="step"><h4>{t('iscrizioni.step2.title', 'Effettua il pagamento')}</h4><p>{t('iscrizioni.step2.text', 'Bonifico bancario secondo le istruzioni fornite.')}</p></div>
                <div className="step"><h4>{t('iscrizioni.step3.title', 'Ricevi conferma')}</h4><p>{t('iscrizioni.step3.text', 'Conferma di tesseramento via e-mail con i dettagli.')}</p></div>
                <div className="step"><h4>{t('iscrizioni.step4.title', 'In sella!')}</h4><p>{t('iscrizioni.step4.text', 'Sei ufficialmente parte di Insolita Sardegna.')}</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sec sec--ink" id="form">
        <div className="wrap">
          <div className="sec-head reveal">
            <div><span className="eyebrow on-dark">{t('iscrizioni.form.eyebrow', 'Modulo di iscrizione')}</span><h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(t('iscrizioni.form.title', 'Compila\ne invia'))}</h2></div>
            <p className="lead" style={{ maxWidth: '34ch' }}>{t('iscrizioni.form.lead', 'Ti ricontatteremo con tutte le istruzioni per completare il tesseramento.')}</p>
          </div>
          {sent ? (
            <div className="reveal" style={{ maxWidth: '48ch' }}>
              <h3 style={{ color: 'var(--acid)', marginBottom: '.5em' }}>{t('iscrizioni.form.success.title', 'Richiesta inviata ✓')}</h3>
              <p className="lead">{t('iscrizioni.form.success.text', "Grazie! Abbiamo ricevuto la tua richiesta di tesseramento e ti abbiamo inviato un'email di riepilogo. Ti ricontatteremo con le istruzioni per completare il pagamento.")}</p>
            </div>
          ) : (
            <form className="reveal" onSubmit={submit}>
              <div className="form-grid">
                <div className="field"><label htmlFor="i-nome">{tr('if.nome')}</label><input id="i-nome" value={form.firstName} onChange={set('firstName')} required /></div>
                <div className="field"><label htmlFor="i-cognome">{tr('if.cognome')}</label><input id="i-cognome" value={form.lastName} onChange={set('lastName')} required /></div>
                <div className="field"><label htmlFor="i-tel">{tr('if.phone')}</label><input id="i-tel" type="tel" value={form.phone} onChange={set('phone')} required /></div>
                <div className="field"><label htmlFor="i-email">{tr('if.email')}</label><input id="i-email" type="email" value={form.email} onChange={set('email')} required /></div>
                <div className="field full"><label htmlFor="i-msg">{tr('if.msg')}</label><textarea id="i-msg" value={form.message} onChange={set('message')} placeholder={tr('if.msg.ph')} /></div>
              </div>
              <label className="consent"><input type="checkbox" checked={form.consent} onChange={set('consent')} required /> {tr('if.consent')}</label>
              {/* Honeypot antispam: nascosto agli utenti umani (position off-screen +
                  aria-hidden + tabindex=-1), i bot generici lo compilano e vengono
                  scartati lato BE. autoComplete off per evitare auto-fill browser. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Non compilare questo campo
                  <input type="text" tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={set('website')} />
                </label>
              </div>
              <TurnstileWidget onToken={handleCaptcha} theme="dark" />
              {error && <p style={{ color: '#ff6b6b', marginTop: 14 }}>{error}</p>}
              <button type="submit" disabled={sending || !captchaToken} className="btn btn-acid" style={{ marginTop: 26, opacity: (sending || !captchaToken) ? 0.6 : 1 }}>{sending ? tr('if.submitting') : tr('if.submit')}</button>
            </form>
          )}
        </div>
      </section>
    </>
  );
};

export default Iscrizioni;
